// Socket.IO client service for real-time notifications
import { io } from 'socket.io-client';
import authService from './authService';

const SOCKET_URL =
  process.env.REACT_APP_SOCKET_URL ||
  window.location.origin;
const SOCKETS_ENABLED = process.env.REACT_APP_ENABLE_SOCKETS !== 'false';
const SOCKET_TRANSPORTS = String(process.env.REACT_APP_SOCKET_TRANSPORTS || 'polling,websocket')
  .split(',')
  .map((value) => value.trim())
  .filter(Boolean);

const isDev = process.env.NODE_ENV !== 'production';
const devLog = (...args) => {
  if (isDev) {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
};
const devError = (...args) => {
  if (isDev) {
    // eslint-disable-next-line no-console
    console.error(...args);
  }
};

class SocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
  }

  syncListeners() {
    if (!this.socket) return;

    this.listeners.forEach((callbacks, event) => {
      callbacks.forEach((callback) => {
        this.socket.off(event, callback);
        this.socket.on(event, callback);
      });
    });
  }

  connect() {
    if (!SOCKETS_ENABLED) {
      devLog('Sockets disabled via REACT_APP_ENABLE_SOCKETS=false');
      return;
    }

    const token = authService.getToken();
    
    if (!token) {
      devLog('No auth token found, skipping socket connection');
      return;
    }

    if (this.socket?.connected || this.socket?.active) {
      return;
    }

    this.socket = io(SOCKET_URL, {
      auth: { token },
      transports: SOCKET_TRANSPORTS.length > 0 ? SOCKET_TRANSPORTS : ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      randomizationFactor: 0.5,
      timeout: 10000
    });

    this.socket.on('connect', () => {
      devLog('Socket connected:', this.socket.id);
      this.syncListeners();
    });

    this.socket.on('disconnect', (reason) => {
      devLog('Socket disconnected:', reason);
    });

    this.socket.on('connect_error', (error) => {
      devError('Socket connection error:', error.message);
    });

  }

  disconnect() {
    if (this.socket) {
      this.listeners.forEach((callbacks, event) => {
        callbacks.forEach((callback) => this.socket.off(event, callback));
      });
      this.socket.disconnect();
      this.socket = null;
    }
  }

  subscribeToTicket(ticketId) {
    if (this.socket?.connected) {
      this.socket.emit('subscribe_ticket', ticketId);
    }
  }

  unsubscribeFromTicket(ticketId) {
    if (this.socket?.connected) {
      this.socket.emit('unsubscribe_ticket', ticketId);
    }
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);

    // Attach to socket if connected
    if (this.socket) {
      this.socket.off(event, callback);
      this.socket.on(event, callback);
    }
  }

  off(event, callback = null) {
    if (callback) {
      if (this.listeners.has(event)) {
        this.listeners.get(event).delete(callback);
        if (this.listeners.get(event).size === 0) {
          this.listeners.delete(event);
        }
      }
    } else {
      this.listeners.delete(event);
    }

    if (this.socket?.connected) {
      if (callback) {
        this.socket.off(event, callback);
      } else {
        this.socket.off(event);
      }
    }
  }

  // Convenience methods for common events
  onTicketCreated(callback) {
    this.on('ticket:created', callback);
  }

  onTicketUpdated(callback) {
    this.on('ticket:updated', callback);
  }

  onTicketAssigned(callback) {
    this.on('ticket:assigned', callback);
  }

  onCommentAdded(callback) {
    this.on('comment:added', callback);
  }

  onNotification(callback) {
    this.on('notification', callback);
  }

  onConnectedUsers(callback) {
    this.on('connected_users', callback);
  }
}

// Export singleton instance
const socketService = new SocketService();
export default socketService;
